import React from 'react';
import { Box, Container, Grid, Typography, Paper } from '@mui/material';
import { Public, TrendingUp, CalendarMonth } from '@mui/icons-material';
import { brandColors } from '../../theme';

interface ValuePropCardProps {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  footnote?: string;
}

const ValuePropCard: React.FC<ValuePropCardProps> = ({ icon, title, subtitle, footnote }) => (
  <Paper
    sx={{
      p: 4,
      height: '100%',
      textAlign: 'center',
      backgroundColor: brandColors.white,
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      borderRadius: 2,
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: `0 8px 24px rgba(0,0,0,0.12), 0 0 0 2px ${brandColors.gold}`,
      },
    }}
  >
    <Box
      sx={{
        display: 'inline-flex',
        p: 2,
        borderRadius: '50%',
        backgroundColor: `${brandColors.gold}15`,
        mb: 3,
      }}
    >
      {React.cloneElement(icon as React.ReactElement<any>, {
        sx: { fontSize: 48, color: brandColors.gold }
      })}
    </Box>
    
    <Typography
      variant="h5"
      sx={{
        color: brandColors.indigo,
        fontWeight: 700,
        mb: 1.5,
      }}
    >
      {title}
    </Typography>
    
    <Typography
      variant="body1"
      sx={{
        color: brandColors.mediumGray,
        lineHeight: 1.6,
        mb: footnote ? 2 : 0,
      }}
    >
      {subtitle}
    </Typography>
    
    {footnote && (
      <Typography
        variant="caption"
        sx={{
          color: brandColors.mediumGray,
          display: 'block',
          mt: 2,
          lineHeight: 1.4,
          fontSize: '0.75rem',
        }}
      >
        {footnote}
      </Typography>
    )}
  </Paper>
);

export const ValueProposition: React.FC = () => {
  const valueProps = [
    {
      icon: <Public />,
      title: '600+ Markets Worldwide*',
      subtitle: 'From major cities to emerging destinations',
      footnote: '*Currently over 150 North American markets are available with another 450 rolling out in Fall 2025',
    },
    {
      icon: <TrendingUp />,
      title: '17 Comprehensive Sections',
      subtitle: 'Deep-dive insights from industry veterans',
    },
    {
      icon: <CalendarMonth />,
      title: 'Updated Monthly',
      subtitle: 'Stay ahead with the latest market dynamics',
    },
  ];

  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: brandColors.white,
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {valueProps.map((prop, index) => (
            <Grid item xs={12} md={4} key={index}>
              <ValuePropCard
                icon={prop.icon}
                title={prop.title}
                subtitle={prop.subtitle}
                footnote={prop.footnote}
              />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};